import React, { useEffect, useMemo, useState } from 'react';
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useTranslation } from '@refinedev/core';
import {
  Box,
  Button,
  Flex,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core';
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_ShowHideColumnsButton,
  MRT_SortingState,
  MRT_TableOptions,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
} from 'mantine-react-table';
import { useSearchParams } from 'react-router-dom';
import {
  BillingDataGridWrapper,
  InternalRefreshButton,
  TableAlertBanner,
  TableProgressBar,
} from 'components/common';
import {
  claimQueryResourceKeys,
  useGetClaimQuery,
  useGetClaimQueryStatusesCount,
  usePaginateClaimQueriesBySite,
  usePollClaimQuery,
} from 'api/resource';
import { type ClaimQueryRecord } from '@billing/validation/claimQuery';
import { ClaimQueryColumns } from 'columns/ClaimQuery';
import { useCurrentSiteStore } from 'store';
import { useProviderAllSites } from 'hooks/useProviderAllSites';
import { useIsProvider } from 'hooks/useIsProvider';
import { useCanModify, useCanView } from 'hooks';
import { claimQueryKeys } from 'api/resource/claim';
import {
  convertFiltersToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';
import { isDateRangeFilterID, normalizeDateRangeFilter } from 'utils/dateUtils';
import { useQueryClient } from '@tanstack/react-query';
import { IconFilterX } from '@tabler/icons-react';
import { QUERIES_WINDOW_TARGET } from 'utils/crossTabNavigation';
import { QueryStatusQuickFilters } from './components/QueryStatusQuickFilters';
import { ClaimQueryDetailsModal } from './components/ClaimQueryDetailsModal';
import {
  applyClaimQueryStatusGroup,
  ClaimQueryStatusGroup,
  getActiveClaimQueryStatusGroup,
  getClaimQueryStatusGroupCounts,
  isClaimQueryStatusesCountResult,
} from './components/queryStatus';

type QueryPageState = {
  pageIndex: number;
  pageSize: number;
  globalFilter: string;
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
};

const LINKED_CLAIM_FILTER_ID = 'claimID';

const parseDateRangeValue = (id: string, value: string) => {
  const [start, end] = value.split('~');
  if (!start || !end) return value;

  return normalizeDateRangeFilter({ id, value: [start, end] }).value;
};

const parseColumnFiltersFromSearchParams = (
  searchParams: URLSearchParams
): MRT_ColumnFiltersState => {
  const filtersParam = searchParams.get('filters')?.trim() ?? '';
  const columnFilters: MRT_ColumnFiltersState = [];

  if (filtersParam) {
    for (const token of filtersParam.split(',')) {
      const [id, ...rest] = token.split(':');
      const rawValue = rest.join(':').trim();
      if (!id || !rawValue || id === 'siteID') continue;

      columnFilters.push({
        id,
        value: isDateRangeFilterID(id)
          ? parseDateRangeValue(id, rawValue)
          : rawValue,
      });
    }
  }

  const claimID = searchParams.get(LINKED_CLAIM_FILTER_ID)?.trim();
  if (
    claimID &&
    !columnFilters.some((filter) => filter.id === LINKED_CLAIM_FILTER_ID)
  ) {
    columnFilters.push({ id: LINKED_CLAIM_FILTER_ID, value: claimID });
  }

  return columnFilters;
};

const parseSortingFromSearchParams = (
  searchParams: URLSearchParams
): MRT_SortingState => {
  const sortingParam = searchParams.get('sorting')?.trim() ?? '';
  if (!sortingParam) return [];

  return sortingParam
    .split(',')
    .map((token) => {
      const [id, descValue] = token.split(':');
      if (!id) return null;
      return {
        id,
        desc: descValue === 'true',
      };
    })
    .filter(Boolean) as MRT_SortingState;
};

const buildInitialState = (searchParams: URLSearchParams): QueryPageState => ({
  pageIndex: Number(searchParams.get('page') ?? '0') || 0,
  pageSize: Number(searchParams.get('pageSize') ?? '25') || 25,
  globalFilter: searchParams.get('globalFilter') ?? '',
  columnFilters: parseColumnFiltersFromSearchParams(searchParams),
  sorting: parseSortingFromSearchParams(searchParams),
});

export const QueryManagement: React.FC = () => {
  const { translate } = useTranslation();
  useDocumentTitle('Queries | QBilling');

  useEffect(() => {
    window.name = QUERIES_WINDOW_TARGET;
    return () => {
      if (window.name === QUERIES_WINDOW_TARGET) window.name = '';
    };
  }, []);

  const queryClient = useQueryClient();
  const computedColorScheme = useComputedColorScheme('dark');
  const { canModify: canModifyQueries } = useCanModify('queries');
  const { canView: canViewClaims } = useCanView('claims');
  const { timezone } = useCurrentSiteStore();
  const isProvider = useIsProvider();
  const { siteID } = useProviderAllSites({ enabled: isProvider });
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryPageState, setQueryPageState] = useState<QueryPageState>(() =>
    buildInitialState(searchParams)
  );
  const [tableInstanceKey, setTableInstanceKey] = useState(0);
  const [selectedQueryID, setSelectedQueryID] = useState<string | null>(
    searchParams.get('queryId')
  );
  const [refreshingQueryID, setRefreshingQueryID] = useState<string | null>(
    null
  );

  const claimQueriesResult = usePaginateClaimQueriesBySite(
    siteID,
    queryPageState.pageIndex,
    queryPageState.pageSize,
    queryPageState.columnFilters,
    queryPageState.globalFilter,
    queryPageState.sorting
  );
  const countsResult = useGetClaimQueryStatusesCount(
    siteID,
    queryPageState.columnFilters,
    queryPageState.globalFilter
  );
  const selectedClaimQueryResult = useGetClaimQuery({
    id: selectedQueryID ?? '',
    enabled: !!selectedQueryID,
    displayNotification: false,
  });
  const pollClaimQuery = usePollClaimQuery();

  const claimQueries = claimQueriesResult.data?.data ?? [];
  const totalQueries = claimQueriesResult.data?.total ?? 0;
  const selectedClaimQuery =
    selectedClaimQueryResult.data ??
    claimQueries.find((claimQuery) => claimQuery.id === selectedQueryID) ??
    null;
  const activeStatusGroup = getActiveClaimQueryStatusGroup(
    queryPageState.columnFilters
  );
  const statusGroupCounts = useMemo(
    () =>
      getClaimQueryStatusGroupCounts(
        isClaimQueryStatusesCountResult(countsResult.data)
          ? countsResult.data
          : undefined
      ),
    [countsResult.data]
  );

  const filtersForQueryString = useMemo(
    () =>
      queryPageState.columnFilters.filter(
        (filter) => filter.id !== LINKED_CLAIM_FILTER_ID
      ),
    [queryPageState.columnFilters]
  );

  useEffect(() => {
    const nextParams = new URLSearchParams();
    nextParams.set('page', String(queryPageState.pageIndex));
    nextParams.set('pageSize', String(queryPageState.pageSize));

    if (queryPageState.globalFilter.trim()) {
      nextParams.set('globalFilter', queryPageState.globalFilter.trim());
    }

    const filterString = convertFiltersToQueryString(filtersForQueryString);
    if (filterString) {
      nextParams.set('filters', filterString);
    }

    const sortingString = convertSortingToQueryString(queryPageState.sorting);
    if (sortingString) {
      nextParams.set('sorting', sortingString);
    }

    if (selectedQueryID) {
      nextParams.set('queryId', selectedQueryID);
    }

    const linkedClaimFilter = queryPageState.columnFilters.find(
      (filter) => filter.id === LINKED_CLAIM_FILTER_ID
    );
    const linkedClaimValue =
      typeof linkedClaimFilter?.value === 'string'
        ? linkedClaimFilter.value.trim()
        : '';
    if (linkedClaimValue) {
      nextParams.set(LINKED_CLAIM_FILTER_ID, linkedClaimValue);
    }

    setSearchParams(nextParams, { replace: true });
  }, [
    queryPageState.columnFilters,
    queryPageState.globalFilter,
    queryPageState.pageIndex,
    queryPageState.pageSize,
    queryPageState.sorting,
    selectedQueryID,
    filtersForQueryString,
    setSearchParams,
  ]);

  const handleSelectQuery = (queryID: string) => {
    setSelectedQueryID(queryID);
  };

  const handleCloseDrawer = () => {
    setSelectedQueryID(null);
  };

  const handleStatusGroupChange = (group: ClaimQueryStatusGroup) => {
    setQueryPageState((prev) => ({
      ...prev,
      columnFilters: applyClaimQueryStatusGroup(prev.columnFilters, group),
      pageIndex: 0,
    }));
  };

  const handleClearFilters = () => {
    setQueryPageState((prev) => ({
      ...prev,
      globalFilter: '',
      columnFilters: [],
      sorting: [],
      pageIndex: 0,
    }));
  };

  const handlePollQuery = async (queryID: string) => {
    try {
      setRefreshingQueryID(queryID);
      await pollClaimQuery.mutateAsync({ id: queryID });
      await Promise.all([
        claimQueriesResult.refetch(),
        countsResult.refetch(),
        selectedQueryID === queryID
          ? selectedClaimQueryResult.refetch()
          : Promise.resolve(),
      ]);
      await queryClient.invalidateQueries({
        queryKey: claimQueryResourceKeys.all,
      });
      await queryClient.invalidateQueries({ queryKey: claimQueryKeys.all });
    } finally {
      setRefreshingQueryID(null);
    }
  };

  const hasColumnFilters = queryPageState.columnFilters.some((filter) => {
    const { value } = filter;
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) {
      return value.some((item) =>
        typeof item === 'string' ? item.trim().length > 0 : item != null
      );
    }
    return true;
  });
  const hasGlobalFilterValue = Boolean(queryPageState.globalFilter.trim());
  const isClearFiltersActive = hasColumnFilters || hasGlobalFilterValue;
  const toolbarIconColor = isClearFiltersActive
    ? 'white'
    : computedColorScheme === 'dark'
    ? 'white'
    : 'black';

  const columns = useMemo(
    () => ClaimQueryColumns(timezone, translate, canViewClaims),
    [timezone, translate, canViewClaims]
  );

  const tableOptions: MRT_TableOptions<ClaimQueryRecord> = {
    data: claimQueries,
    columns,
    manualFiltering: true,
    manualPagination: true,
    enableRowSelection: false,
    enableColumnPinning: true,
    enableColumnDragging: false,
    enableHiding: false,
    enableToolbarInternalActions: true,
    positionToolbarAlertBanner: 'none',
    enableFullScreenToggle: false,
    enableGlobalFilter: true,
    initialState: {
      density: 'md',
      columnFilters: queryPageState.columnFilters,
      showColumnFilters: true,
      showGlobalFilter: true,
      sorting: queryPageState.sorting,
    },
    state: {
      pagination: {
        pageIndex: queryPageState.pageIndex,
        pageSize: queryPageState.pageSize,
      } as MRT_PaginationState,
      globalFilter: queryPageState.globalFilter,
      columnFilters: queryPageState.columnFilters,
      sorting: queryPageState.sorting,
      showAlertBanner: claimQueriesResult.isError,
      showLoadingOverlay:
        claimQueriesResult.isLoading || claimQueriesResult.isFetching,
      showProgressBars:
        claimQueriesResult.isLoading || claimQueriesResult.isFetching,
    },
    rowCount: totalQueries,
    onPaginationChange: (updater) =>
      setQueryPageState((prev) => {
        const nextPagination =
          typeof updater === 'function'
            ? updater({
                pageIndex: prev.pageIndex,
                pageSize: prev.pageSize,
              })
            : updater;

        return {
          ...prev,
          pageIndex: nextPagination.pageIndex,
          pageSize: nextPagination.pageSize,
        };
      }),
    onColumnFiltersChange: (updater) =>
      setQueryPageState((prev) => {
        const nextFilters =
          typeof updater === 'function' ? updater(prev.columnFilters) : updater;

        return {
          ...prev,
          columnFilters: nextFilters,
          pageIndex: 0,
        };
      }),
    onGlobalFilterChange: (updater) =>
      setQueryPageState((prev) => {
        const nextValue =
          typeof updater === 'function' ? updater(prev.globalFilter) : updater;

        return {
          ...prev,
          globalFilter: nextValue ?? '',
          pageIndex: 0,
        };
      }),
    onSortingChange: (updater) =>
      setQueryPageState((prev) => {
        const nextSorting =
          typeof updater === 'function' ? updater(prev.sorting) : updater;

        return {
          ...prev,
          sorting: nextSorting,
          pageIndex: 0,
        };
      }),
    mantineToolbarAlertBannerProps: () =>
      TableAlertBanner({
        isError: claimQueriesResult.isError,
        message: claimQueriesResult.error?.message ?? 'Unable to load queries.',
      }),
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineSearchTextInputProps: {
      placeholder: translate(
        'pages:queries.table.globalSearch',
        'Search claim #, patient, provider, PHIN, tariff, or microfilm'
      ),
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ width: 'fit-content', maxWidth: '100%' }}>
        <QueryStatusQuickFilters
          activeGroup={activeStatusGroup}
          onGroupChange={handleStatusGroupChange}
          statusGroupCounts={statusGroupCounts}
        />
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Flex align="center" gap="0.313rem">
        <InternalRefreshButton queryClient={queryClient} />
        <Tooltip label="Clear filters" withArrow>
          <Button
            onClick={() => {
              handleClearFilters();
              table.resetSorting(true);
              table.resetColumnFilters(true);
              table.resetGlobalFilter(true);
              table.setShowColumnFilters(true);
              table.setShowGlobalFilter(false);
              setTableInstanceKey((current) => current + 1);
            }}
            aria-label="Clear filters"
            data-testid="clear-query-filters-button"
            variant="subtle"
            color={isClearFiltersActive ? 'blue' : 'gray'}
            size="xs"
            p={0}
            style={{
              width: 32,
              height: 32,
              minWidth: 32,
              ...(isClearFiltersActive
                ? {
                    backgroundColor: 'var(--mantine-color-blue-7)',
                    border: '1px solid var(--mantine-color-blue-5)',
                  }
                : {}),
            }}
          >
            <IconFilterX size={24} color={toolbarIconColor} />
          </Button>
        </Tooltip>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
      </Flex>
    ),
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => handleSelectQuery(row.original.id),
      style: {
        cursor: 'pointer',
      },
    }),
  };

  return (
    <>
      <BillingDataGridWrapper
        key={tableInstanceKey}
        title={translate('pages:queries.pageTitle', 'Claim Queries')}
        dataTestID="queries-management"
        tableOptions={tableOptions}
        showAlertBanner={false}
      />
      <ClaimQueryDetailsModal
        opened={Boolean(selectedQueryID)}
        onClose={handleCloseDrawer}
        claimQuery={selectedClaimQuery}
        isLoading={
          Boolean(selectedQueryID) && selectedClaimQueryResult.isFetching
        }
        canRefresh={canModifyQueries}
        canViewClaims={canViewClaims}
        isRefreshing={refreshingQueryID === selectedClaimQuery?.id}
        onRefresh={handlePollQuery}
        timezone={timezone}
      />
    </>
  );
};
